/** 
 * 由Swagger自动生成的API
 * 标签: 产品管理
 */
import request from '../request';
import type { AxiosRequestConfig } from 'axios';
import type { CreateProductDto, Product, UpdateProductDto, PinProductDto, BatchPinProductDto } from './data-contracts';

/**
* 创建新产品
*/
export const apiProductsPOSTAPI = async (data: CreateProductDto, options: AxiosRequestConfig = {}): Promise<Product> => {
  return request({
    url: '/api/products',
    method: 'POST',
    data,
    ...options
  });
};

/** 产品列表查询参数 */
export interface ProductQueryParams {
  page?: number;
  pageSize?: number;
  keyword?: string;
  operator?: string;
  status?: string;
  external_status?: string;
  source_platform?: string;
  is_hidden_from_client?: boolean;
  duplicate_status?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface ProductListResponse {
  items: Product[];
  total: number;
  current: number;
  pageSize: number;
}

/**
* 获取产品列表
*/
export const apiProductsGETAPI = async (query?: ProductQueryParams, options: AxiosRequestConfig = {}): Promise<ProductListResponse> => {
  return request({
    url: '/api/products',
    method: 'GET',
    params: query,
    ...options
  });
};

/**
* 通过ID获取产品
*/
export const apiProductsByIdGETAPI = async (params: { id: number }, options: AxiosRequestConfig = {}): Promise<Product> => {
  return request({
    url: `/api/products/${encodeURIComponent(params.id)}`,
    method: 'GET',
    ...options
  });
};

/**
* 更新产品信息
*/
export const apiProductsByIdPATCHAPI = async (params: { id: number }, data: UpdateProductDto, options: AxiosRequestConfig = {}): Promise<Product> => {
  return request({
    url: `/api/products/${encodeURIComponent(params.id)}`,
    method: 'PATCH',
    data,
    ...options
  });
};

/**
* 删除产品
*/
export const apiProductsByIdDELETEAPI = async (params: { id: number }, options: AxiosRequestConfig = {}): Promise<any> => {
  return request({
    url: `/api/products/${encodeURIComponent(params.id)}`,
    method: 'DELETE',
    ...options
  });
};

/**
* 更新产品状态
*/
export const apiProductsByIdStatusPATCHAPI = async (
  params: { id: number }, 
  data: { status: '在售' | '已下架' } | { data: { status: '在售' | '已下架' } }, 
  options: AxiosRequestConfig = {}
): Promise<Product> => {
  // 处理可能在data或data.data中的status
  const requestData = 'data' in data ? data.data : data;
  
  return request({
    url: `/api/products/${encodeURIComponent(params.id)}/status`,
    method: 'PATCH',
    data: requestData,
    ...options
  });
};

/**
* 更新产品客户端展示状态
*/
export const apiProductsByIdClientVisibilityPATCHAPI = async (
  params: { id: number },
  data: { is_hidden_from_client: boolean; reason?: string },
  options: AxiosRequestConfig = {}
): Promise<Product> => {
  return request({
    url: `/api/products/${encodeURIComponent(params.id)}/client-visibility`,
    method: 'PATCH',
    data,
    ...options
  });
};

/**
 * 同步外部产品数据
 * @returns 同步结果
 */
export const apiProductsSyncexternalPOSTAPI = async function (): Promise<{
  count: number;
  totalSynced: number;
  created: number;
  updated: number;
  marked: number;
  appSupplement?: {
    totalProducts: number;
    updatedProducts: number;
    totalPages: number;
    errors: string[];
    skippedProducts: number;
    activeGroups: Array<{
      active: string;
      totalProducts: number;
      updatedProducts: number;
      skippedProducts: number;
      totalPages: number;
    }>;
  } | null;
  errors: string[];
}> {
  return request({
    url: '/api/products/sync-external',
    method: 'POST',
    timeout: 10 * 60 * 1000,
  });
};

/**
 * 更新产品置顶状态
 * @param id - 产品ID
 * @param data - 置顶信息
 * @returns 更新后的产品
 */
export const apiProductsByIdPinPATCHAPI = async function (
  { id }: { id: number },
  data: PinProductDto
): Promise<Product> {
  return request({
    url: `/api/products/${id}/pin`,
    method: 'PATCH',
    data,
  });
};

/**
 * 获取外部产品详情
 * @param id - 本地产品ID
 * @returns 外部产品完整详情（所有字段均有中文描述）
 */
export const apiProductsByIdExternalDetailGETAPI = async function (
  { id }: { id: number }
): Promise<any> {
  return request({
    url: `/api/products/${id}/external-detail`,
    method: 'GET',
  });
};

/**
 * 批量更新产品置顶状态
 * @param data - 批量置顶信息
 * @returns 操作结果
 */
export const apiProductsBatchPinPOSTAPI = async function (
  data: BatchPinProductDto
): Promise<{ success: boolean; message: string }> {
  return request({
    url: '/api/products/batch-pin',
    method: 'POST',
    data,
  });
};
